/**
 * Payment recovery — the driver app's side (sw-ev-api
 * docs/PAYMENT_RECOVERY.md, "Driver app").
 *
 * These are ordinary session-guarded calls: the default axios instance,
 * with the session's cookie / the driver's Bearer like every other
 * /ev/sessions/:uid/... request. The payment PAGE has its own client
 * (payLinkApi.js); nothing here is shared with it.
 *
 * A failure is reduced to `{ ok: false }`: the link response carries a
 * payment credential, so no response or error object is ever logged.
 */
import axios from 'axios';

const SESSIONS_BASE = `${import.meta.env.VITE_API_URL}/ev/sessions`;

const base = (sessionUid) => `${SESSIONS_BASE}/${encodeURIComponent(sessionUid)}/payment-recovery`;

/** Is this session's customer being asked to confirm a card again? */
export async function getPaymentRecoveryPrompt(sessionUid) {
  try {
    const res = await axios.get(base(sessionUid));
    const data = res?.data;
    if (!data || data.ok !== true) return { ok: false };
    return { ok: true, prompt: data };
  } catch {
    return { ok: false };
  }
}

/** The prompt is on screen. This is how staff know it reached the customer. */
export async function markPaymentRecoveryPromptSeen(sessionUid) {
  try {
    await axios.post(`${base(sessionUid)}/seen`);
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

/** A short-lived payment-page link for this customer. */
export async function createPaymentRecoveryLink(sessionUid) {
  try {
    const res = await axios.post(`${base(sessionUid)}/link`);
    const url = res?.data?.ok === true ? res.data.url : null;
    return typeof url === 'string' && url ? { ok: true, url } : { ok: false };
  } catch {
    return { ok: false };
  }
}
